// ----------------------------------------------------------------------
// <copyright file="Izenda.DatabaseMappingConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import MappingTenant from 'logic/models/Izenda.MappingTenant';
import { join } from 'lodash/fp';

const ALL_TENANT_ID = 'AllTenantId';
const MAPPING_TYPE = {
  Schema: 1,
  Database: 2
};

/**
 * Build mapping key.
 * @param {Object} sourceData 
 */
function buildMappingKey(sourceData) {
  // Same with optionFromDatabaseNameValue.
  return String.format(
    '{0} - [{1}] {2} - {3}',
    sourceData.databaseServer,
    sourceData.serverTypeName,
    sourceData.databaseName,
    sourceData.databaseUser
  );
}

export default {
  get TABLE_DEF() {
    return {
      fromDatabaseName: {
        key: 'fromDatabaseName',
        columnKey: 'fromDatabaseName',
        width: 150,
        title: localizer.getText('DATABASE_MAPPING_LABEL_TABLE_DEF_GLOBAL_MAPPING_FROM_DB_NAME')
      },
      type: {
        key: 'type',
        columnKey: 'type',
        width: 150,
        title: localizer.getText('COMMON_LABEL_TYPE')
      },
      fromObject: {
        key: 'fromObject',
        columnKey: 'fromObject',
        width: 150,
        title: localizer.getText('COMMON_LABEL_OBJECT')
      },
      toDatabaseName: {
        key: 'toDatabaseName',
        columnKey: 'toDatabaseName',
        width: 150,
        title: localizer.getText('DATABASE_MAPPING_LABEL_TABLE_DEF_GLOBAL_MAPPING_TO_DB_NAME')
      },
      toObject: {
        key: 'toObject',
        columnKey: 'toObject',
        width: 150,
        title: localizer.getText('COMMON_LABEL_OBJECT'),
        infoIconOptions: {
          iconClass: 'icon-info-circled',
          iconTitle: localizer.getText('COPY_MANAGEMENT_OBJECT_TOOLTIP')
        }
      },
      tenant: {
        key: 'mappingTenants',
        columnKey: 'mappingTenants',
        width: 200,
        title: localizer.getText('DATABASE_MAPPING_LABEL_TENANT_COLUMN')
      },
      action: {
        width: 60,
        fixed: true,
        align: 'center',
        title: localizer.getText('COMMON_LABEL_ACTION'),
        className: 'align-bottom'
      }
    };
  },
  get REQUIRE_CONSTRAINT() {
    return {
      presence: {
        presence: true,
        message: localizer.getText('COMMON_CONSTRAINT_REQUIRED')
      }
    };
  },
  get MAPPING_TYPES() {
    return [
      {
        label: localizer.getText('COPY_MANAGEMENT_LABEL_MAPPING_TYPE_SCHEMA'),
        value: MAPPING_TYPE.Schema
      },
      {
        label: localizer.getText('COPY_MANAGEMENT_LABEL_MAPPING_TYPE_DATABASE'),
        value: MAPPING_TYPE.Database
      }
    ];
  },
  MAPPING_TYPE,
  MAPPING_HEIGHT: 40,
  ALL_TENANT_ID,
  ALL_TENANT_OPTION: new MappingTenant({
    id: ALL_TENANT_ID,
    name: localizer.getText('DATABASE_MAPPING_ALL_TENANTS')
  }),
  ALL_MAPPINGS_OPTION: new MappingTenant({
    id: ALL_TENANT_ID,
    name: localizer.getText('DATABASE_MAPPING_ALL_MAPPINGS')
  }),
  DATABASE_NAME_OPTION_MAPPINGS: [
    {
      source(sourceData) {
        return buildMappingKey(sourceData);
      },
      target: 'key' // Unique key for lazy loading
    },
    {
      source(sourceData) {
        return buildMappingKey(sourceData);
      },
      target: 'valueField' // Key for option in drop-down
    },
    {
      source(sourceData) {
        return String.format('[{0}] {1}', sourceData.serverTypeName, sourceData.databaseName);
      },
      target: 'labelField' // Display value, also value to communicate with api.
    },
    {
      source(sourceData) {
        return join('\n', sourceData.distinctKey);
      },
      target: 'optionTooltip' // Tooltip value
    },
    {
      source(sourceData) {
        return String.format(
          '{0} - [{1}] {2} - {3}', 
          sourceData.databaseServer,
          sourceData.serverTypeName, 
          sourceData.databaseName,
          sourceData.name
        );
      },
      target: 'connectionKey'
    },
    {
      source(sourceData) {
        return sourceData.databaseServer;
      },
      target: 'databaseServer' // Server information.
    },
    {
      source(sourceData) {
        return sourceData.databaseUser;
      },
      target: 'databaseUser' // Database User ID.
    }
  ],
  DATABASE_NAME_KEY: 'valueField',
  OBJECT_OPTION_MAPPINGS: [
    {
      source(sourceData) {
        return sourceData;
      },
      target: 'key'
    },
    {
      source(sourceData) {
        return sourceData;
      },
      target: 'valueField'
    },
    {
      source(sourceData) {
        return sourceData;
      },
      target: 'labelField'
    }
  ],
  OBJECT_KEY: 'valueField',
  DEFAULT_PAGE_INDEX: 1,
  DEFAULT_PAGE_SIZE: 10,
  ID_PREFIX: 'temp_',
  MAPPING_HASH_PATTERN: '{0}#{1}#{2}#{3}#{4}#{5}#{6}#{7}#{8}'
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.DatabaseMappingConstants.js